<template>
  <InputField
    :tag="tag"
    :id="id"
    :label="label"
    :prefix="prefix"
    :required="required"
    :invalid-feedback="invalidFeedback ?? errorMessage"
    :value="value"
    @input="handleChange"
  >
    <template v-if="$slots.label" #label>
      <slot name="label"></slot>
    </template>
    <template v-if="$slots.prefix" #prefix>
      <slot name="prefix"></slot>
    </template>
  </InputField>
</template>

<script lang="ts">
export type TextFieldProps = InputFieldProps & {
  maxLength?: number | string;
  modelValue?: string | null;
  minLength?: number | string;
  name?: string;
  required?: boolean;
  rules?: StringSchema;
  type?: string;
};

export type TextFieldEvents = {
  (event: "update:modelValue", value: string): unknown;
};
</script>

<script lang="ts" setup>
import { uniqueId } from "underscore";
import { useField } from "vee-validate";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { StringSchema, string } from "yup";
import InputField, { InputFieldProps } from "./InputField.vue";

const props = withDefaults(defineProps<TextFieldProps>(), {
  id: uniqueId("field-"),
  maxLength: Infinity,
  minLength: 0,
  type: "text",
});
defineEmits<TextFieldEvents>();

const { t } = useI18n();

const rules = computed<StringSchema<string | undefined>>(() => {
  let schema = string();

  if (props.rules) {
    schema = schema.concat(props.rules);
  }
  if (props.required) {
    schema = schema.required(t("form.field.text.invalid.required"));
  }
  schema = schema.max(
    Number(props.maxLength),
    t("form.field.text.invalid.required", { n: props.maxLength })
  );
  schema = schema.min(
    Number(props.minLength),
    t("form.field.text.invalid.required", { n: props.minLength })
  );

  return schema;
});

const { value, errorMessage, handleChange } = useField<
  string | null | undefined
>(() => props.name ?? props.id, rules, {
  syncVModel: true,
});
</script>
